import React, { useContext, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

import { UGCInflowSource, UGCReviewRow } from '@/common/model/ugcView'

import UGCReviewImportPanel from './UGCReviewImportPanel'

import { fetchRest } from '@/common/api/fetcher'

// カード共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
// 入力欄は標準 TextField / Textarea と同じトークンで揃える（枠: border-input）
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const PANEL_INPUT = 'w-full rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

interface StatusStyle {
  badge: string
  accent: string
  label: string
}

const STATUS_MAP: Record<string, StatusStyle> = {
  published: { badge: 'bg-surface-success text-success', accent: 'var(--color-fill-success)', label: '公開' },
  hidden: { badge: 'bg-surface-critical text-critical', accent: 'var(--color-fill-critical)', label: '非公開' },
  // 2026-09-29 修正：呼び名をバックエンドの定義（services/ugc_status.py）に合わせた。
  // 以前は pending を「保留」、later を「後で」と出しており、「未確認 N件」と一覧の表示が結びつかなかった
  pending: { badge: 'bg-surface-warning text-warning', accent: 'var(--color-fill-warning)', label: '未確認' },
  later: { badge: 'bg-surface-emphasis text-emphasis', accent: 'var(--color-fill-emphasis)', label: '保留' },
}

/** 「あり／なし」の絞り込みの値（API へは has_xxx=true/false で渡す） */
type PresenceFilter = '' | 'yes' | 'no'

const INTERNAL_TAG_OPTIONS = ['商品開発', '物流・配送', 'CS対応', 'サイト改善', 'マーケティング', 'ポジティブ', 'ネガティブ', 'ピックアップ']

export default function UGCReviewManagement() {
  const { siteId } = useParams()
  // 他画面（コンテンツ鮮度アラート等）から ?review_id= 付きで開かれたら、その1件だけ出す
  const [searchParams, setSearchParams] = useSearchParams()
  const reviewIdParam = searchParams.get('review_id') || ''
  const { addMessage } = useContext(ToastMessageContext)
  const [reviews, setReviews] = useState<UGCReviewRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [ratingFilter, setRatingFilter] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [pendingCount, setPendingCount] = useState(0)
  // 流入経路（区分）。レビューは依頼メール・CSV取り込み・W2Commerce連携から入る
  const [inflowFilter, setInflowFilter] = useState('')
  const [inflowSources, setInflowSources] = useState<UGCInflowSource[]>([])
  const [inflowCounts, setInflowCounts] = useState<Record<string, number>>({})
  const [showImport, setShowImport] = useState(false)
  // 2026-09-29 追加：返信・メモ・タグの有無で絞る
  const [commentFilter, setCommentFilter] = useState<PresenceFilter>('')
  const [memoFilter, setMemoFilter] = useState<PresenceFilter>('')
  const [publishing, setPublishing] = useState(false)

  const fetchReviews = () => {
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), per_page: '12' })
    if (statusFilter) params.set('status', statusFilter)
    if (inflowFilter) params.set('inflow_source', inflowFilter)
    if (ratingFilter) { params.set('min_rating', ratingFilter); params.set('max_rating', ratingFilter) }
    if (search) params.set('search', search)
    if (commentFilter) params.set('has_shop_comment', String(commentFilter === 'yes'))
    if (memoFilter) params.set('has_internal_memo', String(memoFilter === 'yes'))
    // タグは API で絞る（以前は画面側で1ページ分だけ絞っており、件数・ページ送りと食い違っていた）
    if (tagFilter === '__any') params.set('has_internal_tags', 'true')
    else if (tagFilter === '__none') params.set('has_internal_tags', 'false')
    else if (tagFilter) params.set('internal_tag', tagFilter)
    if (reviewIdParam) params.set('review_id', reviewIdParam)
    fetchRest<{
      items?: UGCReviewRow[]
      total?: number
      inflow_sources?: UGCInflowSource[]
      inflow_counts?: Record<string, number>
      status_counts?: Record<string, number>
    }>(`/api/sites/${siteId}/ugc/reviews?${params}`)
      .then(d => {
        setReviews(d.items || [])
        setTotal(d.total || 0)
        // 経路の選択肢と件数はサーバ側を正とする（画面で文言を持つと一覧と割れる）
        setInflowSources(d.inflow_sources || [])
        setInflowCounts(d.inflow_counts || {})
        setPendingCount(d.status_counts?.pending ?? 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchReviews()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, page, statusFilter, ratingFilter, inflowFilter, tagFilter, commentFilter, memoFilter, search, reviewIdParam])

  // 一覧に今出ている未確認のレビュー。一括公開の対象はこれだけ（画面に無いものは公開しない）
  const visiblePending = reviews.filter(r => r.status === 'pending')
  const showingPendingOnly = statusFilter === 'pending'

  /** 「未確認 N件」を押したら未確認だけの一覧にする（他の絞り込みは外す） */
  const showPendingOnly = () => {
    setStatusFilter('pending')
    setRatingFilter('')
    setInflowFilter('')
    setTagFilter('')
    setCommentFilter('')
    setMemoFilter('')
    setSearch('')
    setSearchInput('')
    setPage(1)
    if (reviewIdParam) setSearchParams({})
  }

  const handleBulkPublish = () => {
    const ids = visiblePending.map(r => r.id)
    if (!ids.length) return
    // 顧客サイトに出る操作なので、件数を示して確認する
    if (!window.confirm(`表示中の未確認レビュー ${ids.length}件を公開します。\nサイトのレビュー枠に表示されるようになります。よろしいですか？`)) return
    setPublishing(true)
    fetchRest<{ updated?: number }>(`/api/sites/${siteId}/ugc/reviews/bulk-update`, {
      method: 'POST',
      body: JSON.stringify({ review_ids: ids, status: 'published' }),
    })
      .then(res => {
        const n = res.updated ?? ids.length
        addMessage({
          text: `${n}件のレビューを公開しました（未確認 → 公開）。サイトのレビュー枠に表示されます`,
          type: MessageType.SUCCESS
        })
        fetchReviews()
      })
      .catch(() => addMessage({ text: '公開できませんでした。時間をおいて再度お試しください', type: MessageType.CRITICAL }))
      .finally(() => setPublishing(false))
  }

  const totalPages = Math.ceil(total / 12)

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='投稿レビュー管理'
        description='寄せられたレビューの審査・掲載・返信・内部管理を行います'
      />

      <div className='mb-4 flex justify-end'>
        <Button color={ButtonColor.SECONDARY} onClick={() => setShowImport(v => !v)}>
          レビューを取り込む
        </Button>
      </div>

      {showImport && (
        <UGCReviewImportPanel
          siteId={siteId}
          onImported={fetchReviews}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* 2026-09-29 修正：件数から未確認だけの一覧へ移れるようにし、一括公開の対象と結果を明示した */}
      {pendingCount > 0 && (
        <div
          className={`${CARD} mb-4 flex flex-wrap items-center justify-between gap-2 px-5 py-3`}
          style={{ borderLeft: '4px solid var(--color-fill-warning)' }}
        >
          <div className='flex items-center gap-2'>
            <span className='text-lg'>📋</span>
            <span className='text-primary text-sm font-semibold'>
              まだ確認していないレビューが <span className='text-warning'>{pendingCount}件</span> あります
            </span>
            {!showingPendingOnly && (
              <Button color={ButtonColor.SECONDARY} onClick={showPendingOnly}>
                未確認だけ表示
              </Button>
            )}
          </div>
          <div className='flex items-center gap-2'>
            <span className='text-tertiary text-[11px]'>
              {visiblePending.length > 0
                ? '下に表示中の未確認レビューだけを公開します'
                : '表示中に未確認のレビューはありません'}
            </span>
            <Button
              color={ButtonColor.PRIMARY}
              disabled={visiblePending.length === 0 || publishing}
              loading={publishing}
              onClick={handleBulkPublish}
            >
              表示中の未確認 {visiblePending.length}件を公開
            </Button>
          </div>
        </div>
      )}

      {reviewIdParam && (
        <div className={`${CARD} mb-4 flex items-center justify-between px-5 py-2.5`}>
          <span className='text-secondary text-xs'>レビュー #{reviewIdParam} だけを表示しています</span>
          <Button color={ButtonColor.SECONDARY} onClick={() => setSearchParams({})}>
            すべてのレビューを表示
          </Button>
        </div>
      )}

      {/* フィルタ */}
      <div className={`${CARD} mb-4 flex flex-wrap items-center gap-2.5 p-3`}>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>全ステータス</option>
          <option value='published'>公開</option>
          <option value='hidden'>非公開</option>
          <option value='pending'>未確認</option>
          <option value='later'>保留</option>
        </select>
        <select value={ratingFilter} onChange={e => { setRatingFilter(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>全評価</option>
          {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
        </select>
        <select value={inflowFilter} onChange={e => { setInflowFilter(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>全経路</option>
          {inflowSources
            .filter(o => (inflowCounts[o.value] ?? 0) > 0)
            .map(o => (
              <option key={o.value} value={o.value}>
                {o.label}（{inflowCounts[o.value] ?? 0}）
              </option>
            ))}
        </select>
        <select value={tagFilter} onChange={e => { setTagFilter(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>内部タグ：すべて</option>
          <option value='__any'>内部タグあり</option>
          <option value='__none'>内部タグなし</option>
          {INTERNAL_TAG_OPTIONS.map(t => <option key={t} value={t}>タグ：{t}</option>)}
        </select>
        <select value={commentFilter} onChange={e => { setCommentFilter(e.target.value as PresenceFilter); setPage(1) }} className={INPUT}>
          <option value=''>ショップ返信：すべて</option>
          <option value='yes'>ショップ返信あり</option>
          <option value='no'>ショップ返信なし</option>
        </select>
        <select value={memoFilter} onChange={e => { setMemoFilter(e.target.value as PresenceFilter); setPage(1) }} className={INPUT}>
          <option value=''>内部メモ：すべて</option>
          <option value='yes'>内部メモあり</option>
          <option value='no'>内部メモなし</option>
        </select>
        <div className='flex gap-1.5'>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (setSearch(searchInput), setPage(1))}
            placeholder='検索...'
            className={`${INPUT} w-40`}
          />
          <Button color={ButtonColor.SECONDARY} onClick={() => { setSearch(searchInput); setPage(1) }}>
            検索
          </Button>
        </div>
        <span className='text-secondary ml-auto text-xs'>{total}件</span>
      </div>

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : reviews.length === 0 ? (
        <div className={`${CARD} text-secondary p-16 text-center text-sm`}>レビューがありません</div>
      ) : (
        <div className='grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-3.5'>
          {reviews.map(r => (
            <ReviewCard key={r.id} review={r} siteId={siteId} onRefresh={fetchReviews} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className='mt-5 flex items-center justify-center gap-3'>
          <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            ← 前へ
          </Button>
          <span className='text-secondary text-xs'>{page} / {totalPages}</span>
          <Button color={ButtonColor.SECONDARY} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            次へ →
          </Button>
        </div>
      )}
    </Main>
  )
}

interface ReviewCardProps {
  review: UGCReviewRow
  siteId: string | undefined
  onRefresh(): void
}

function ReviewCard({ review, siteId, onRefresh }: ReviewCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    title: review.title || '',
    body: review.body || '',
    // ★は編集中に必ず1つ選ばせる。null のままだと比較のたびに判定が要る
    rating: review.rating ?? 0,
  })
  const [shopComment, setShopComment] = useState(review.shop_comment || '')
  const [showComment, setShowComment] = useState(false)
  const [internalTags, setInternalTags] = useState<string[]>(review.internal_tags || [])
  const [showTags, setShowTags] = useState(false)
  const [memo, setMemo] = useState(review.internal_memo || '')
  const [showMemo, setShowMemo] = useState(false)
  const r = review
  const sc = STATUS_MAP[r.status] || STATUS_MAP.pending
  const isMember = r.reviewer_type === 'member' || r.is_verified_purchase

  const handleUpdate = (data: Record<string, unknown>) => {
    fetchRest(`/api/sites/${siteId}/ugc/reviews/${r.id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }).then(() => onRefresh())
  }

  const handleSaveEdit = () => {
    handleUpdate({ title: editForm.title, body: editForm.body, rating: editForm.rating })
    setEditing(false)
  }

  const handleSaveComment = () => {
    handleUpdate({ shop_comment: shopComment })
    setShowComment(false)
  }

  const toggleTag = (tag: string) => {
    const next = internalTags.includes(tag) ? internalTags.filter(t => t !== tag) : [...internalTags, tag]
    setInternalTags(next)
    handleUpdate({ internal_tags: next })
  }

  const handleSaveMemo = () => {
    handleUpdate({ internal_memo: memo })
    setShowMemo(false)
  }

  return (
    <div className={`${CARD} overflow-hidden p-0`} style={{ borderTop: `3px solid ${sc.accent}` }}>
      {/* ヘッダー */}
      <div className='flex items-center justify-between px-4 pt-3 pb-2'>
        <span className='text-secondary max-w-[55%] truncate text-xs font-semibold'>
          {r.product_external_id || '商品未指定'}
        </span>
        <div className='flex items-center gap-1.5'>
          {/* 流入経路。依頼メール経由は既定の経路なので出さず、それ以外だけ示す
              （全カードに同じバッジが並ぶと、区別したい経路が埋もれる） */}
          {r.inflow_source && r.inflow_source !== 'request_mail' && (
            <span className='bg-surface-secondary text-secondary rounded-[10px] px-2 py-0.5 text-[10px] font-bold whitespace-nowrap'>
              {r.inflow_source_label}
              {r.inflow_detail ? `・${r.inflow_detail}` : ''}
            </span>
          )}
          <span className={`${sc.badge} rounded-[10px] px-2.5 py-0.5 text-[10px] font-bold`}>
            {sc.label}
          </span>
        </div>
      </div>

      {/* 星評価 */}
      <div className='px-4 pb-1.5'>
        {editing ? (
          <div className='flex gap-1'>
            {[1, 2, 3, 4, 5].map(n => (
              <span
                key={n}
                onClick={() => setEditForm({ ...editForm, rating: n })}
                className={`cursor-pointer text-[22px] ${n <= editForm.rating ? 'text-warning' : 'text-disabled'}`}
              >★</span>
            ))}
          </div>
        ) : (
          <span className='text-warning text-xl tracking-[2px]'>
            {'★'.repeat(r.rating || 0)}{'☆'.repeat(5 - (r.rating || 0))}
          </span>
        )}
      </div>

      {/* 投稿者 */}
      <div className='flex items-center gap-2 px-4 pb-2'>
        <div
          className={`flex h-[30px] w-[30px] items-center justify-center rounded-full text-xs font-bold text-on-bg-fill ${isMember ? 'bg-fill-brand' : 'bg-fill-tertiary'}`}
        >
          {isMember ? '会' : 'ゲ'}
        </div>
        <div>
          <div className='text-primary text-[13px] font-semibold'>{r.reviewer_name || '匿名'}</div>
          <div className='text-tertiary text-[10px]'>
            {/* 取り込んだレビューは created_at が全件ほぼ同時刻になるため、
                元の投稿日時があればそちらを出す */}
            {(r.posted_at || r.created_at)?.slice(0, 10)}
            {isMember ? ' · 会員' : ' · ゲスト'}
          </div>
        </div>
        {r.is_verified_purchase && (
          <span className='bg-surface-success text-success ml-auto rounded-lg px-2 py-0.5 text-[9px] font-semibold'>
            ✓ 購入確認済
          </span>
        )}
      </div>

      {/* レビュー本文 */}
      <div className='px-4 pb-2.5'>
        {editing ? (
          <>
            <input
              value={editForm.title}
              onChange={e => setEditForm({ ...editForm, title: e.target.value })}
              placeholder='タイトル'
              className={`${PANEL_INPUT} mb-1.5`}
            />
            <textarea
              value={editForm.body}
              onChange={e => setEditForm({ ...editForm, body: e.target.value })}
              className={`${PANEL_INPUT} h-20 resize-y`}
            />
          </>
        ) : (
          <>
            {r.title && <div className='text-primary mb-1 text-[13px] font-semibold'>{r.title}</div>}
            <p
              className='text-secondary m-0 overflow-hidden text-xs leading-relaxed'
              style={{ maxHeight: expanded ? 'none' : 60 }}
            >
              {r.body || '(本文なし)'}
            </p>
            {(r.body || '').length > 100 && (
              <button onClick={() => setExpanded(!expanded)} className='text-link cursor-pointer py-1 text-[11px]'>
                {expanded ? '▲ 閉じる' : '▼ もっと見る'}
              </button>
            )}
          </>
        )}
      </div>

      {/* 添付画像 */}
      {r.image_urls && r.image_urls.length > 0 && (
        <div className='flex gap-1.5 overflow-x-auto px-4 pb-2.5'>
          {r.image_urls.map((url, i) => (
            <img key={i} src={url} alt='' className='border-secondary h-16 w-16 shrink-0 rounded-lg border object-cover' />
          ))}
        </div>
      )}

      {/* 内部タグ */}
      {internalTags.length > 0 && (
        <div className='flex flex-wrap gap-1 px-4 pb-2'>
          {internalTags.map(tag => (
            <span key={tag} className='bg-surface-warning text-warning rounded-[10px] px-2 py-0.5 text-[9px] font-semibold'>{tag}</span>
          ))}
        </div>
      )}

      {/* ショップコメント表示 */}
      {r.shop_comment && !showComment && (
        <div className='bg-surface-success mx-4 mb-2.5 rounded-lg border border-secondary p-2.5'>
          <div className='text-success mb-1 text-[10px] font-semibold'>💬 ショップからの返信</div>
          <p className='text-primary m-0 text-xs leading-normal'>{r.shop_comment}</p>
        </div>
      )}

      {/* 内部メモ。2026-09-29 追加：開かないと見えず、メモがあることにも気づけなかったため常時表示する */}
      {r.internal_memo && !showMemo && (
        <div className='bg-surface-secondary mx-4 mb-2.5 rounded-lg border border-dashed border-secondary p-2.5'>
          <div className='text-secondary mb-1 text-[10px] font-semibold'>📝 内部メモ（お客様には表示されません）</div>
          <p className='text-primary m-0 line-clamp-2 text-xs leading-normal'>{r.internal_memo}</p>
        </div>
      )}

      {/* AIスコア */}
      {r.ai_score != null && (
        <div className='px-4 pb-2'>
          <span
            className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${
              r.ai_score >= 70
                ? 'bg-surface-success text-success'
                : r.ai_score >= 40
                  ? 'bg-surface-warning text-warning'
                  : 'bg-surface-critical text-critical'
            }`}
          >AI: {r.ai_score}点</span>
        </div>
      )}

      {/* アクションバー */}
      <div className='bg-surface-secondary flex flex-wrap items-center gap-1 border-t border-secondary px-4 py-2.5'>
        {/* ステータス4ボタン */}
        {['published', 'hidden', 'pending', 'later'].map(s => {
          const sm = STATUS_MAP[s]
          const active = r.status === s
          return (
            <button
              key={s}
              onClick={() => !active && handleUpdate({ status: s })}
              className={`rounded-md px-2 py-1 text-[10px] font-semibold ${
                active ? `${sm.badge} cursor-default` : 'text-tertiary hover:bg-surface-hover cursor-pointer'
              }`}
            >{sm.label}</button>
          )
        })}
        <div className='ml-auto flex gap-0.5'>
          <button onClick={() => { setEditing(!editing); if (editing) handleSaveEdit() }} className='hover:bg-surface-hover cursor-pointer rounded p-1 text-sm' title={editing ? '保存' : '編集'}>
            {editing ? '💾' : '✏️'}
          </button>
          {/* 入力済みのものは印を付ける（中身があるかどうかを開かずに分かるように） */}
          <IconToggle icon='💬' title='ショップコメント' filled={!!r.shop_comment} onClick={() => setShowComment(!showComment)} />
          <IconToggle icon='🏷️' title='内部タグ' filled={internalTags.length > 0} onClick={() => setShowTags(!showTags)} />
          <IconToggle icon='📝' title='内部メモ' filled={!!r.internal_memo} onClick={() => setShowMemo(!showMemo)} />
        </div>
      </div>

      {/* ショップコメント入力 */}
      {showComment && (
        <div className='bg-surface-secondary border-t border-secondary px-4 pt-2.5 pb-3.5'>
          <div className='text-secondary mb-1.5 text-[11px] font-semibold'>💬 ショップコメント</div>
          <textarea value={shopComment} onChange={e => setShopComment(e.target.value)} placeholder='お客様への返信コメント...' className={`${PANEL_INPUT} h-[60px] resize-y`} />
          <div className='mt-1.5 flex gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={handleSaveComment}>保存</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => setShowComment(false)}>閉じる</Button>
          </div>
        </div>
      )}

      {/* 内部タグ選択 */}
      {showTags && (
        <div className='bg-surface-secondary border-t border-secondary px-4 pt-2.5 pb-3.5'>
          <div className='text-secondary mb-2 text-[11px] font-semibold'>🏷️ 内部管理タグ</div>
          <div className='flex flex-wrap gap-1.5'>
            {INTERNAL_TAG_OPTIONS.map(tag => {
              const on = internalTags.includes(tag)
              return (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`cursor-pointer rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                    on ? 'bg-surface-warning text-warning border-transparent' : 'bg-surface text-secondary border-secondary'
                  }`}
                >{tag}</button>
              )
            })}
          </div>
        </div>
      )}

      {/* 内部メモ */}
      {showMemo && (
        <div className='bg-surface-secondary border-t border-secondary px-4 pt-2.5 pb-3.5'>
          <div className='text-secondary mb-1.5 text-[11px] font-semibold'>📝 内部メモ</div>
          <textarea value={memo} onChange={e => setMemo(e.target.value)} placeholder='社内用メモ（お客様には表示されません）' className={`${PANEL_INPUT} h-[50px] resize-y`} />
          <div className='mt-1.5 flex gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={handleSaveMemo}>保存</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => setShowMemo(false)}>閉じる</Button>
          </div>
        </div>
      )}
    </div>
  )
}

/** アクションバーのアイコン。入力済みなら右上に点を付ける */
function IconToggle({ icon, title, filled, onClick }: { icon: string; title: string; filled: boolean; onClick(): void }) {
  return (
    <button
      onClick={onClick}
      className='hover:bg-surface-hover relative cursor-pointer rounded p-1 text-sm'
      title={filled ? `${title}（入力済み）` : title}
    >
      {icon}
      {filled && <span className='bg-fill-emphasis absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full' />}
    </button>
  )
}
